type InstallOutcome = 'accepted' | 'dismissed' | 'manual' | 'unavailable';

type BeforeInstallPromptChoice = {
  outcome: 'accepted' | 'dismissed';
  platform: string;
};

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<BeforeInstallPromptChoice>;
};

export type PwaInstallState = {
  canUsePrompt: boolean;
  isInstalled: boolean;
  isIos: boolean;
  isMobile: boolean;
};

type Listener = (state: PwaInstallState) => void;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let initialized = false;
let isInstalled = false;

const listeners = new Set<Listener>();

const getNavigator = (): Navigator | undefined => {
  if (typeof window === 'undefined') return undefined;

  return window.navigator;
};

const getIsIos = (): boolean => {
  const navigatorRef = getNavigator();

  if (!navigatorRef) return false;

  const userAgent = navigatorRef.userAgent.toLowerCase();
  const platform = navigatorRef.platform?.toLowerCase() ?? '';

  return (
    /iphone|ipad|ipod/.test(userAgent) ||
    (platform === 'macintel' && navigatorRef.maxTouchPoints > 1)
  );
};

const getIsMobile = (): boolean => {
  if (typeof window === 'undefined') return false;

  return window.matchMedia('(max-width: 760px), (pointer: coarse)').matches;
};

const getIsStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;

  const navigatorRef = window.navigator as Navigator & {
    standalone?: boolean;
  };

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigatorRef.standalone === true
  );
};

const getState = (): PwaInstallState => {
  return {
    canUsePrompt: Boolean(deferredPrompt),
    isInstalled,
    isIos: getIsIos(),
    isMobile: getIsMobile(),
  };
};

const notify = () => {
  const nextState = getState();
  listeners.forEach((listener) => listener(nextState));
};

export const initPwaInstall = () => {
  if (typeof window === 'undefined' || initialized) return;

  initialized = true;
  isInstalled = getIsStandalone();

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    isInstalled = true;
    notify();
  });

  window
    .matchMedia('(display-mode: standalone)')
    .addEventListener?.('change', () => {
      isInstalled = getIsStandalone();
      notify();
    });
};

export const subscribePwaInstall = (listener: Listener): (() => void) => {
  initPwaInstall();
  listeners.add(listener);
  listener(getState());

  return () => {
    listeners.delete(listener);
  };
};

export const installPwa = async (): Promise<InstallOutcome> => {
  initPwaInstall();

  if (isInstalled) return 'accepted';

  if (!deferredPrompt) {
    return getIsIos() ? 'manual' : 'unavailable';
  }

  const promptEvent = deferredPrompt;
  deferredPrompt = null;
  await promptEvent.prompt();

  const choice = await promptEvent.userChoice;

  if (choice.outcome === 'accepted') {
    isInstalled = true;
  }

  notify();

  return choice.outcome;
};
